// Durable records contain only composition metadata, never photos, URLs or tokens.
const ID = /^[A-Za-z0-9_-]{1,128}$/;
export function usageFingerprint(value) {
  const canonical = (item) => Array.isArray(item) ? item.map(canonical)
    : item && typeof item === "object" ? Object.keys(item).sort().reduce((result, key) => {
      result[key] = canonical(item[key]); return result;
    }, Object.create(null)) : item;
  return JSON.stringify(canonical(value));
}
export function validUsageManifest(value) {
  return Boolean(value && value.versao === 1 && ID.test(value.compraId || "") && ID.test(value.totemId || "")
    && Object.keys(value).every(key => ["versao", "compraId", "totemId", "tipoFoto", "codigoProduto", "quantidadeFotosPedido", "fotos", "saidas", "unidade"].includes(key))
    && Array.isArray(value.saidas) && value.saidas.length > 0 && value.saidas.length <= 500
    && value.saidas.every(id => ID.test(id)) && new Set(value.saidas).size === value.saidas.length
    && Array.isArray(value.fotos) && value.fotos.length > 0 && value.fotos.length <= 500
    && value.fotos.every(photo => photo && ID.test(photo.fotoId || "")
      && Object.keys(photo).every(key => ["fotoId", "saidaId", "molduraId", "origem", "nome", "global"].includes(key))
      && (photo.nome === null || photo.nome === undefined || typeof photo.nome === "string" && photo.nome.length <= 160)));
}
export function createUsageOutbox(storage, project, uid, limits = {}) {
  if (!storage || !ID.test(project || "") || typeof uid !== "string" || !uid || uid.length > 128) return null;
  const prefix = `photonow.molduras-aceite.v1:${encodeURIComponent(project)}:${encodeURIComponent(uid)}:`;
  const maxItems = limits.maxItems || 100, maxBytes = limits.maxBytes || 2 * 1024 * 1024;
  const keys = () => {
    const result = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key?.startsWith(prefix)) result.push(key);
    }
    return result;
  };
  const parse = raw => { try { return JSON.parse(raw); } catch (_) { return null; } };
  const keyFor = (kind, id) => `${prefix}${kind}:${id}`;
  const store = (key, value) => {
    const serialized = JSON.stringify(value), allKeys = keys();
    const bytes = allKeys.filter(item => item !== key).reduce((sum, item) => sum + (storage.getItem(item)?.length || 0) * 2, 0) + serialized.length * 2;
    if (serialized.length > 256 * 1024 || bytes > maxBytes || !storage.getItem(key) && allKeys.length >= maxItems) throw new Error("Fila local de uso de molduras sem espaço.");
    storage.setItem(key, serialized);
    if (storage.getItem(key) !== serialized) throw new Error("Não foi possível confirmar a persistência do aceite.");
    return value;
  };
  return {
    prepare(manifest) {
      if (!validUsageManifest(manifest)) return false;
      store(keyFor("preparo", manifest.totemId), { manifest }); return true;
    },
    clearPrepared() {
      keys().filter(key => key.startsWith(`${prefix}preparo:`)).forEach(key => storage.removeItem(key));
    },
    prepared(totemId, compraId) {
      const value = parse(storage.getItem(keyFor("preparo", totemId)));
      return validUsageManifest(value?.manifest) && value.manifest.compraId === compraId ? value.manifest : null;
    },
    enqueue(record) {
      if (!validUsageManifest(record?.manifest) || record.saidasAceitas !== record.manifest.saidas.length || !Number.isFinite(Date.parse(record.aceitoEm))) return null;
      const key = keyFor("aceite", record.manifest.compraId), old = parse(storage.getItem(key));
      if (old) {
        if (usageFingerprint(old.manifest) !== usageFingerprint(record.manifest) || old.saidasAceitas !== record.saidasAceitas) {
          const error = new Error("Aceite anterior diferente: mantenha a ocorrência para conferência.");
          error.code = "USAGE_CONFLICT"; throw error;
        }
        return old;
      }
      return store(key, { versao: 1, manifest: record.manifest, saidasAceitas: record.saidasAceitas, aceitoEm: record.aceitoEm });
    },
    pending(totemId) {
      return keys().filter(key => key.startsWith(`${prefix}aceite:`)).map(key => parse(storage.getItem(key)))
        .filter(value => validUsageManifest(value?.manifest) && value.manifest.totemId === totemId
          && value.saidasAceitas === value.manifest.saidas.length && !value.revisao)
        .sort((a, b) => a.aceitoEm.localeCompare(b.aceitoEm));
    },
    acknowledge(record) {
      const key = keyFor("aceite", record.manifest.compraId);
      if (storage.getItem(key) === JSON.stringify(record)) storage.removeItem(key);
    },
    review(record) {
      const key = keyFor("aceite", record.manifest.compraId);
      if (storage.getItem(key) === JSON.stringify(record)) store(key, { ...record, revisao: true });
    },
  };
}
